<template>
  <div
    v-on="$listeners"
    class="editor-wrapper"
    @keydown.esc.capture="onEscape"
    :class="[
      styleType,
      {
        'single-line': isSingleLine,
        'no-heading-margins': noHeadingMargins,
        'is-readonly': isReadonly,
        'is-export': isExport,
        'placeholder-on-focus-only': showPlaceholderOnFocusOnly && !focused,
        'placeholder-hidden-on-focus': hidePlaceholderOnFocus
      }
    ]"
  >
    <bubble-menu
      :editor="editor"
      v-if="useFloatingMenu && editor"
      :tippyOptions="tippyOptions"
      :should-show="shouldShowBubbleMenu"
    >
      <TextStylingBar
        :editor="editor"
        :showSend="false"
        :showOverlay="true"
        :hideActions="hideActions"
        :hideTextTypes="hideTextTypes"
      />
    </bubble-menu>
    <editor-content v-if="editor" :editor="editor" ref="editor" />
    <div
      v-else
      ref="staticRoot"
      class="ProseMirror static-content"
      v-html="staticContentHtml"
      @mousedown.capture="onStaticMousedown"
      @click.capture="onStaticClick"
      @mouseover="onStaticMouseover"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-2'
import { dismissBubbleMenu, isBubbleMenuDismissed, isNodeSelection, isTabSelection } from '@/util/tiptap-tab-selection'
import type { EditorEvents } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'
import TextEditorActions, { Action } from './TextEditorActions.vue'
import TextStylingBar, { TextType } from './TextStylingBar.vue'
import { openVariableSuggestionAt, openVariableSuggestionAtView } from '@/util/tiptap-variable-suggestion'
import { getEditorViewFromDom } from '@/util/tiptap-editor-view-registry'
import { emptyStaticHtmlWithPlaceholder, isEmptyStaticHtml } from '@/util/tiptap-static-render'
import { setEditorPlaceholder, toPlaceholderText } from '@/util/tiptap-placeholder'
import {
  EMBED_CARD_CLASS,
  hydrateEmbedCard,
  isCardLoading,
  isEmbedLoaded,
  loadEmbedBehindCard,
  markEmbedLoaded,
  srcEmbedKey,
  watchCardThumbnail
} from '@/util/embed-click-to-load'
import { openInNewTabWithFeedback } from '@/util/open-in-new-tab'
import { applyLinkOpenHint } from '@/util/tiptap-link'
import { normalizeLinkHref } from '@/util/link-href'
import { hydratePrintCards, PRINT_CARD_CLASS } from '@/util/embed-print-card'
import { mapStores } from 'pinia'
import { ElementHistoryStore, useElementHistoryStore } from '@/modules/elements/stores/ElementHistoryStore'
import { useElementStore } from '@/modules/elements/stores/ElementStore'
import ElementReversibleAction from '@/modules/elements/models/ElementReversibleAction'
import { uuid } from 'vue-uuid'
import { AutosaveManager } from '@/util/AutosaveManager'
import { useSaveCoordinatorStore } from '@/modules/elements/stores/SaveCoordinatorStore'
import { enqueueSaveFailedSnackbar } from '@/util/api-error-snackbar'
import { normalizeVariablePlaceholderDom } from '@/util/variable-placeholder-dom'
import { requestVariableAssignment } from '@/util/variable-assignment-bus'
import { historyKey } from '@/util/prosemirror-history'
import { useAppStore } from '@/modules/global/stores/AppStore'
import { isExportMode } from '@/util/canvas-render-mode'
import { usePermissionStore } from '@/modules/auth/stores/PermissionStore'

/** Identity of the cell or block a pooled editor is showing. The pool assigns this synchronously. */
export type PooledEditorTarget = {
  editorId: string
  elementId: string
  saveAction: () => Promise<void>
  readonly: boolean
  placeholder: string
  historyEntity: string
}

/** Module-level so the bubble menu receives a stable object instead of a fresh one on every render. */
const BUBBLE_MENU_TIPPY_OPTIONS = {
  placement: 'top-start',
  theme: 'none',
  duration: 250,
  // Mount outside the editor's scroll container so a wide menu cannot add a scrollbar of its own.
  appendTo: () => document.body,
  // The toolbar caps itself at the viewport width; tippy's default 350px would wrap it on wide screens too.
  maxWidth: 'none',
  popperOptions: {
    modifiers: [
      // Slide along the edge instead of overflowing it, and flip when there is no room above or below.
      { name: 'preventOverflow', options: { padding: 8, altAxis: true, boundary: 'viewport' } },
      {
        name: 'flip',
        options: { padding: 8, fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'] }
      }
    ]
  }
}

@Component({
  // Declared so the name survives minification: VariableAssignmentProvider finds live editors by it
  name: 'TextEditor',
  components: {
    BubbleMenu,
    EditorContent,
    TextEditorActions,
    TextStylingBar
  },
  computed: {
    ...mapStores(useElementHistoryStore)
  }
})
export default class TextEditor extends Vue {
  // Read once, not through the store: a subscription per editor would fan out from a flag fixed before mount
  private isExport = isExportMode()

  /**
   * May be null until the parent activates it (lazy table cells); `staticHtml` is shown meanwhile.
   * Typed non-null because every other parent always passes an editor.
   */
  @Prop() editor!: Editor
  @Prop({ default: '', type: String }) staticHtml!: string
  @Prop() private placeholder!: string
  @Prop({ default: false }) private useFloatingMenu!: boolean
  @Prop({ default: true }) private showPlaceholderOnFocusOnly!: boolean
  @Prop({ default: () => [] }) private hideActions!: Action[]
  @Prop({ default: () => [] }) private hideTextTypes!: TextType[]
  @Prop({ type: Boolean }) focused?: boolean
  @Prop({ default: 'medium' }) styleType?: 'large' | 'small' | 'medium'
  @Prop({ type: Boolean, default: false }) isSingleLine!: boolean
  @Prop({ type: Boolean, default: false }) hidePlaceholderOnFocus!: boolean
  @Prop({ default: false, type: Boolean }) readonly!: boolean
  /** When set, identity comes from `setPooledTarget` instead of the props below. */
  @Prop({ default: false, type: Boolean }) pooled!: boolean
  @Prop({ default: async () => {}, type: Function }) saveAction!: () => Promise<void>
  @Prop({ default: '', type: String }) private enableHistoryForEntity!: string
  @Prop({ default: () => uuid.v4(), type: String }) private editorId!: string
  /** The element this editor saves into; while it has unsaved typing, stored content is not pushed back in */
  @Prop({ default: '', type: String }) private saveElementId!: string
  @Prop({ default: false, type: Boolean }) noHeadingMargins!: boolean
  private elementHistoryStore!: ElementHistoryStore
  private autosaveManager!: AutosaveManager
  // Assigned in created, not initialized here, so Vue does not make them reactive
  /** Players loading behind their cards in static content; stopped when that content goes away */
  private embedWaits!: (() => void)[]
  private stopStaticWatch!: (() => void) | null
  private iconSyncRaf: number | null = null
  /** Created on first use: most editors on a page never type, and legacy pages mount a thousand of them */
  private pendingEditKey!: string | undefined
  /**
   * Assigned in created so Vue does not observe it. A reactive target would notify a tick late and the
   * transaction in between would save the new document onto the previous cell.
   */
  private pooledTarget!: PooledEditorTarget | undefined
  /** Mirrors the target's readonly flag so the wrapper class updates. The target itself stays non-reactive. */
  private pooledReadonly = false

  created() {
    this.pooledTarget = undefined
    this.autosaveManager = new AutosaveManager(
      () => this.activeSaveAction(),
      undefined,
      enqueueSaveFailedSnackbar,
      pending => this.reportPendingEdit(pending)
    )
    this.embedWaits = []
    if (this.editor) {
      this.initEditor(this.editor)
    } else {
      // Only a host without an editor yet (a lazy table cell) renders static content; editors get no watcher for it
      this.stopStaticWatch = this.$watch('staticContentHtml', () => this.$nextTick(() => this.restoreLoadedEmbeds()))
    }
  }

  @Watch('editor') onEditorActivated(editor: Editor | null, previous: Editor | null) {
    if (!editor) return
    // An editor replacing another only needs its placeholder; the rest is set up once, on activation
    if (previous) {
      setEditorPlaceholder(editor, this.activePlaceholderText())
      return
    }
    // Static content is gone for good: an activated editor lives until the host unmounts
    this.stopStaticWatch?.()
    this.stopStaticWatch = null
    this.cancelEmbedWaits()
    this.initEditor(editor)
    this.$nextTick(() => this.attachEditorDom())
  }

  @Watch('placeholderText') onPlaceholderChange(text: string) {
    if (this.pooled) return
    if (this.editor) setEditorPlaceholder(this.editor, text)
  }

  /**
   * Points this editor at another cell or block. Capture the previous target first: this stops the timer
   * rather than flushing it, so a pending debounce cannot save the next document with the previous action.
   */
  setPooledTarget(target: PooledEditorTarget): void {
    if (!this.pooled) {
      console.warn('[TextEditor] setPooledTarget ignored because pooled mode is off')
      return
    }
    this.autosaveManager?.stopTimer()
    this.pooledTarget = target
    this.pooledReadonly = target.readonly
    if (!this.editor) return
    // No 'update' event: the document did not change, and update listeners save. Unchanged flags skip the re-render.
    if (this.editor.isEditable === target.readonly) this.editor.setEditable(!target.readonly, false)
    setEditorPlaceholder(this.editor, toPlaceholderText(target.placeholder))
  }

  /** The pool parked: drop the last target, so nothing of its host stays reachable from here. */
  clearPooledTarget(): void {
    if (!this.pooled) return
    this.autosaveManager?.stopTimer()
    this.pooledTarget = undefined
  }

  /** Pooled identity is the target only. Host props arrive a tick late and belong to the previous cell. */
  private activeSaveAction(): Promise<void> {
    if (this.pooled) return this.pooledTarget ? this.pooledTarget.saveAction() : Promise.resolve()
    return this.saveAction()
  }

  private activeEditorId(): string {
    if (this.pooled) return this.pooledTarget?.editorId ?? ''
    return this.editorId
  }

  private activeElementId(): string {
    if (this.pooled) return this.pooledTarget?.elementId ?? ''
    return this.saveElementId
  }

  private activeHistoryEntity(): string {
    if (this.pooled) return this.pooledTarget?.historyEntity ?? ''
    return this.enableHistoryForEntity
  }

  private activePlaceholderText(): string {
    if (this.pooled) return toPlaceholderText(this.pooledTarget?.placeholder)
    return this.placeholderText
  }

  private initEditor(editor: Editor) {
    editor.setEditable(!this.isReadonly)
    setEditorPlaceholder(editor, this.activePlaceholderText())

    editor.on('transaction', this.onEditorTransaction)
    editor.on('blur', this.onEditorBlur)

    // The pool restores a target's history after it installs that target's document, and it registers undo per
    // target. Either one here would write this document's history onto the previous target, or bind undo to
    // whichever target is current when the closure runs.
    if (this.pooled) return
    // Only hosts that pass an editorId use editor history (all of them do). A generated default id is unique per
    // instance and nothing removes registrations, so registering it would keep one closure per mounted editor.
    if (!this.$options.propsData || !('editorId' in this.$options.propsData)) return

    const savedState = this.elementHistoryStore.getEditorState(this.editorId)
    if (savedState) {
      const historySpecState = historyKey.get(editor.state)?.spec?.state
      if (historySpecState && historySpecState.fromJSON) {
        const currentState = editor.view.state as any

        const editorStateConfig = {
          schema: currentState.schema,
          doc: currentState.doc,
          selection: currentState.selection,
          storedMarks: currentState.storedMarks,
          plugins: currentState.plugins
        }

        const deserializedHistoryState = historySpecState.fromJSON(editorStateConfig, savedState, currentState)
        currentState.history$ = deserializedHistoryState
        editor.view.updateState(currentState)
      }
    }
    const undo = () => {
      this.editor?.chain().undo().focus().run()
    }
    const redo = () => {
      this.editor?.chain().redo().focus().run()
    }
    this.elementHistoryStore.saveEditorUndoRedoActions(this.editorId, undo, redo)
  }

  onEditorTransaction(event: EditorEvents['transaction']) {
    const isVariableSync = !!event.transaction.getMeta('variablePlaceholderUpdate')
    const isStoreSync = !!event.transaction.getMeta('preventUpdate')

    const { editor } = event
    const historyEntity = this.activeHistoryEntity()
    if (historyEntity) {
      const historyState = historyKey.getState(editor.state)
      const historySpecState = historyKey.get(editor.state)?.spec?.state
      if (historySpecState && historySpecState.toJSON) {
        const serializedHistoryState = historySpecState.toJSON(historyState)
        this.elementHistoryStore.saveEditorState(this.activeEditorId(), serializedHistoryState)
      }

      if (event.transaction.getMeta('pushToUndoStack')) {
        this.onNewHistoryEvent()
      }

      // Variable placeholder syncs and content set from the store (`preventUpdate`, as TipTap marks a setContent without
      // emitUpdate) are display-only: saving them would echo the server's own copy back and loop
      if (event.transaction.docChanged && editor.isEditable && !isVariableSync && !isStoreSync) {
        this.autosaveManager.startTimer()
      }
    }

    if (event.transaction.docChanged || isVariableSync) {
      this.scheduleVariableIconSync()
    }
  }

  /** Typing the autosave has not queued yet counts as saving (header status), and blocks stored-content syncs. */
  private reportPendingEdit(pending: boolean) {
    if (!this.pendingEditKey) {
      if (!pending) return
      this.pendingEditKey = `text-editor-${uuid.v4()}`
    }
    useSaveCoordinatorStore().setEditPending(this.pendingEditKey, this.activeElementId() || null, pending)
  }

  /**
   * Leaving the editor queues its pending edit at once (S.0 rule 2: capture before switching). Otherwise a save
   * response for an earlier edit could land first, and a host that syncs stored content into an unfocused editor
   * would overwrite the newer typing.
   */
  onEditorBlur() {
    if (this.autosaveManager?.isPending) this.flushPendingSave()
  }

  async onNewHistoryEvent() {
    const historyEntity = this.activeHistoryEntity()
    if (historyEntity) {
      // Captured now: the entry belongs to the target that made the change, even after the pool moves on
      // (0.5 Amendment 1)
      const editorId = this.activeEditorId()
      const reversibleAction = new ElementReversibleAction<void, void>()
      reversibleAction.name = `Update text in ${editorId}`
      reversibleAction.action = async () => {
        this.elementHistoryStore.doRedoForEditor(editorId)
      }
      reversibleAction.reverseAction = async () => {
        this.elementHistoryStore.doUndoForEditor(editorId)
      }
      this.elementHistoryStore.recordAction(historyEntity, reversibleAction)
    }
  }

  /**
   * Saves an edit still waiting on the autosave timer and resolves once it, and any save already in flight, is
   * stored. Before-delete callbacks await this, so a delete never overtakes a save. With nothing pending, no request
   * is sent.
   */
  async savePendingChanges(): Promise<void> {
    await this.autosaveManager?.settle()
  }

  /**
   * Runs a pending autosave now. Parents call this before destroying the editor they own,
   * so the save action still reads content from a live editor.
   */
  flushPendingSave(): void {
    try {
      this.autosaveManager?.flush()
    } catch (error) {
      console.warn('[TextEditor] Failed to flush pending save', { editorId: this.activeEditorId(), error })
    }
  }

  /** Drops a pending autosave without sending it. The pool captures and persists the HTML itself. */
  stopPendingSave(): void {
    this.autosaveManager?.stopTimer()
  }

  /** Whether an edit is still waiting on the autosave timer (pool capture checks this before stopping the timer). */
  hasPendingAutosave(): boolean {
    return !!this.autosaveManager?.isPending
  }

  @Watch('readonly') onReadonlyChange() {
    // Pooled readonly comes from the target, applied in setPooledTarget.
    if (this.pooled) return
    this.editor?.setEditable(!this.readonly)
  }

  /** The prop for a normal editor; the target's flag for a pooled one, so the wrapper class tracks the cell. */
  get isReadonly(): boolean {
    return this.pooled ? this.pooledReadonly : this.readonly
  }

  /** Only valid once an editor exists; parents with a lazy editor must not save before activation. */
  getContent(): string {
    return this.editor!.getHTML()
  }

  get tippyOptions() {
    return BUBBLE_MENU_TIPPY_OPTIONS
  }

  /**
   * Escape closes a showing bubble menu first. It is consumed only then, so with no menu it still does what it did
   * (for example leaving the block), and a second Escape acts as before.
   */
  onEscape(event: KeyboardEvent): void {
    const editor = this.editor
    if (!this.useFloatingMenu || !editor || editor.isDestroyed) return
    if (!this.shouldShowBubbleMenu({ editor, state: editor.state })) return
    event.preventDefault()
    event.stopPropagation()
    dismissBubbleMenu(editor)
  }

  shouldShowBubbleMenu({
    editor,
    state
  }: {
    editor: Editor
    state: { selection: { empty: boolean; from: number; to: number } }
  }): boolean {
    if (!editor || editor.isDestroyed || !editor.isEditable || !editor.isFocused) {
      return false
    }
    // Hide per-editor BubbleMenu while multi-element formatting toolbar is active.
    if (useElementStore().selectedElementsLength > 1) {
      return false
    }
    // Tab through table cells selects each cell's content: the menu waits a moment so Tab can keep moving
    if (isTabSelection(editor, state.selection)) return false
    // Closed with Escape: stays closed until the selection changes
    if (isBubbleMenuDismissed(editor, state.selection)) return false
    // A card or embed just inserted stays selected as a whole: the text toolbar has nothing to format in it
    if (isNodeSelection(state.selection)) return false
    // A caret in a link shows it too: that is how a link is edited (a click places the caret, it does not open it)
    return !state.selection.empty || editor.isActive('link')
  }

  /** Shown by CSS on the empty block (Placeholder extension); the focus props only toggle classes on the wrapper. */
  get placeholderText(): string {
    return toPlaceholderText(this.placeholder)
  }

  get staticContentHtml(): string {
    if (this.placeholderText && isEmptyStaticHtml(this.staticHtml)) {
      return emptyStaticHtmlWithPlaceholder(this.placeholderText)
    }
    return this.staticHtml
  }

  /** Static links in an editable block are edited by a click, so hovering says what Ctrl/Cmd+click does (as in a live editor). */
  onStaticMouseover(event: MouseEvent): void {
    if (this.isReadonly) return
    applyLinkOpenHint(event.target, event.currentTarget as Element)
  }

  /** A press on the callout must not focus or press the block: that swaps the editor in and eats the click. */
  onStaticMousedown(event: MouseEvent): void {
    if (!(event.target as Element | null)?.closest?.('.assign-callout')) return
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Static content (shown until the first click makes it an editor) already offers the assign callout on hover, so
   * its click opens the assignee menu right away; a click-to-load card (tiptap-static-render.ts) loads its embed.
   * Either way the click goes no further, so it does not also activate a lazy cell's editor.
   */
  onStaticClick(event: MouseEvent): void {
    const callout = (event.target as Element | null)?.closest?.('.assign-callout') as HTMLElement | null
    if (callout) {
      event.preventDefault()
      event.stopPropagation()
      this.requestAssignment(callout)
      return
    }
    // A link in an editable block is edited, not followed: the click still goes on and activates the editor.
    // Ctrl/Cmd+click opens it in a new tab, a read-only block follows links, and so does a bookmark card (as in a live editor).
    const link = (event.target as Element | null)?.closest?.('a')
    if (!this.isReadonly && link && !link.closest('.notch-bookmark__link')) {
      event.preventDefault()
      // Opened by us rather than by the browser, so the user gets the same "Opened in new tab" snackbar as in a live editor
      const href = link.getAttribute('href')
      if ((event.ctrlKey || event.metaKey) && href) openInNewTabWithFeedback(normalizeLinkHref(href))
      return
    }
    const card = (event.target as Element | null)?.closest?.(`.${EMBED_CARD_CLASS}`) as HTMLButtonElement | null
    if (!card) return
    event.preventDefault()
    event.stopPropagation()
    const src = card.getAttribute('data-embed-src')
    if (card.disabled || isCardLoading(card) || !src) return
    markEmbedLoaded(srcEmbedKey(src))
    this.embedWaits.push(loadEmbedBehindCard(card, src, true))
  }

  /**
   * v-html renders cards again on every content change: embeds loaded earlier this session stay loaded, and cards
   * get what cached HTML cannot hold (looked-up thumbnails, a fallback for a thumbnail that fails to load, and in
   * the export, print cards' QR codes).
   */
  private restoreLoadedEmbeds(): void {
    const root = this.$refs.staticRoot as HTMLElement | undefined
    // The previous content's cards are gone; so is any wait for their players
    this.cancelEmbedWaits()
    // Export: print cards get their QR code, which cached HTML cannot hold
    if (root && this.staticContentHtml.includes(PRINT_CARD_CLASS)) hydratePrintCards(root)
    // Most static content holds no embed; skip the DOM query for it
    if (!root || !this.staticContentHtml.includes(EMBED_CARD_CLASS)) return
    root
      .querySelectorAll<HTMLButtonElement>(`.${EMBED_CARD_CLASS}[data-embed-src]:not(.${EMBED_CARD_CLASS}--loading)`)
      .forEach(card => {
        const src = card.getAttribute('data-embed-src') as string
        watchCardThumbnail(card)
        hydrateEmbedCard(card)
        // The card shows while the player loads again, rather than an empty frame
        if (isEmbedLoaded(srcEmbedKey(src))) this.embedWaits.push(loadEmbedBehindCard(card, src, false))
      })
  }

  private cancelEmbedWaits(): void {
    this.embedWaits?.splice(0).forEach(cancel => cancel())
  }

  private editorElement: HTMLElement | null = null

  private scheduleVariableIconSync(): void {
    if (!this.editorElement) return
    if (this.iconSyncRaf !== null) return
    this.iconSyncRaf = window.requestAnimationFrame(() => {
      this.iconSyncRaf = null
      this.syncVariablePlaceholderIcons()
    })
  }

  private syncVariablePlaceholderIcons(): void {
    if (!this.editorElement) return
    normalizeVariablePlaceholderDom(this.editorElement)
  }

  /**
   * Handles mousedown events on assign-callout elements to prevent editor interaction
   * @param e - Mouse event
   */
  private handleCalloutMousedown = (e: MouseEvent): void => {
    // Early return if target is not a callout element
    const target = e.target as HTMLElement | null
    if (!target || !target.closest) return

    const callout = target.closest('.assign-callout')
    if (!callout) return

    e.preventDefault()
    e.stopPropagation()
  }

  /**
   * Handles click events on assign-callout elements to prevent editor interaction
   * and trigger recipient assignment modal
   * @param e - Mouse event
   */
  private handleCalloutClick = (e: MouseEvent): void => {
    // Early return if target is not a callout element
    const target = e.target as HTMLElement | null
    if (!target || !target.closest) return

    const callout = target.closest('.assign-callout') as HTMLElement | null
    if (!callout) return

    e.preventDefault()
    e.stopPropagation()

    this.requestAssignment(callout)
  }

  /** Opens the assignee menu for the variable a callout belongs to (the live editor, or null for static content). */
  private requestAssignment(callout: HTMLElement): void {
    // Extract variable information from the callout's parent wrapper
    const variableWrapper = callout.closest('.variable-wrapper')
    if (variableWrapper) {
      const variablePlaceholder = variableWrapper.querySelector('.variable-placeholder') as HTMLElement | null
      if (variablePlaceholder) {
        // Extract variable name from placeholder text (format: {{ variableName }})
        const variableText = variablePlaceholder.textContent || ''
        const match = variableText.match(/\{\{\s*([^}]+?)\s*\}\}/)
        const variableName = match?.[1]?.trim()

        // Extract variable ID from data attribute if available
        const variableId = variablePlaceholder.getAttribute('data-variable-id') || undefined

        if (variableName) {
          // Emit event for parent component to handle recipient assignment
          const payload = {
            variableName,
            variableId,
            element: callout,
            editor: this.editor,
            rootEntityType: this.saveElementId
              ? useElementStore().elementById(this.saveElementId)?.rootEntityType
              : undefined
          }
          // Hosts without their own handler (table cells, FAQ, timeline, ...) use the shared assign menu
          if (this.$listeners['assign-recipient']) this.$emit('assign-recipient', payload)
          else requestVariableAssignment(payload)
        }
      }
    }
  }

  /**
   * Opens variable dropdown when clicking a non-editable placeholder
   * @param e - Mouse event
   */
  private handleVariablePlaceholderClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null
    if (!target || !target.closest) return

    if (target.closest('.assign-callout') || target.closest('.variable-inner')) {
      return
    }

    const placeholder = target.closest('.variable-placeholder') as HTMLElement | null
    if (!placeholder) {
      return
    }

    // Close any open variable action menu when switching variables
    window.dispatchEvent(new CustomEvent('variable-action-menu-close'))

    const isReadOnly = this.isReadonly || (this.editor ? !this.editor.isEditable : false)
    const isPreviewOrExternal = useAppStore().isInPreviewMode || usePermissionStore().isExternalUser()
    if (isPreviewOrExternal) {
      const hasRecipient = placeholder.getAttribute('data-has-recipient') === 'true'
      if (hasRecipient) {
        const assignedToCurrentUser = placeholder.getAttribute('data-assigned-to-current-user') !== 'false'
        this.$emit('variable-fill-request', {
          allowed: assignedToCurrentUser,
          variableId: placeholder.getAttribute('data-variable-id') || undefined,
          variableName: placeholder.getAttribute('data-variable-name') || undefined
        })
        e.preventDefault()
        e.stopPropagation()
        return
      }
    }

    // Non-editable content (e.g. a page locked by a completed signature) must not offer the variable
    // list: no modify/delete/create suggestion, and no internal fill.
    if (isPreviewOrExternal || isReadOnly) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const proseMirrorElement = target.closest('.ProseMirror') as HTMLElement | null
    const view =
      this.editor?.view || getEditorViewFromDom(proseMirrorElement) || getEditorViewFromDom(this.editorElement)
    if (!view) {
      return
    }

    e.preventDefault()
    e.stopPropagation()

    const startPos = view.posAtDOM(placeholder, 0)
    if (startPos === null || startPos < 0) {
      return
    }

    const textContent = placeholder.textContent || ''
    const searchEnd = Math.min(view.state.doc.content.size, startPos + textContent.length + 1)

    const variableName = placeholder.getAttribute('data-variable-name') || ''
    let selectionPos: number | null = null
    view.state.doc.nodesBetween(startPos, searchEnd, (node, pos) => {
      if (!node.isText || !node.marks) {
        return true
      }

      const hasVariableMark = node.marks.some(mark => mark.type.name === 'variablePlaceholder')
      if (!hasVariableMark) {
        return true
      }

      if (variableName && node.text) {
        const nameIndex = node.text.indexOf(variableName)
        if (nameIndex >= 0) {
          selectionPos = Math.min(pos + nameIndex + 1, view.state.doc.content.size)
        }
      }

      if (selectionPos === null && node.text) {
        const braceIndex = node.text.indexOf('{{')
        if (braceIndex >= 0) {
          selectionPos = Math.min(pos + braceIndex + 3, view.state.doc.content.size)
        }
      }

      if (selectionPos === null) {
        selectionPos = Math.min(pos + 1, view.state.doc.content.size)
      }
      return false
    })

    if (selectionPos === null) {
      return
    }

    const { state } = view
    const docSize = state.doc.content.size
    const clampPos = (pos: number) => Math.max(0, Math.min(pos, docSize))
    const hasVariableMarkAtPos = (pos: number) => {
      const $pos = state.doc.resolve(clampPos(pos))
      return $pos.marks().some(mark => mark.type.name === 'variablePlaceholder')
    }
    const clearCandidates = [startPos - 1, startPos + textContent.length + 1, startPos + textContent.length + 2]
    const clearPos = clearCandidates.map(clampPos).find(pos => !hasVariableMarkAtPos(pos))
    const shouldDelayOpen = clearPos !== undefined && clearPos !== state.selection.from

    if (clearPos !== undefined && clearPos !== state.selection.from) {
      view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, clearPos)))
    }

    const openSuggestion = () => {
      if (this.editor) {
        this.editor.commands.focus()
        openVariableSuggestionAt(this.editor, selectionPos!)
      } else {
        view.focus()
        openVariableSuggestionAtView(view, selectionPos!)
      }
    }

    if (shouldDelayOpen) {
      requestAnimationFrame(openSuggestion)
    } else {
      openSuggestion()
    }
  }

  mounted(): void {
    this.attachEditorDom()
    this.restoreLoadedEmbeds()
  }

  private attachEditorDom(): void {
    // Cache editor element reference for better performance
    this.editorElement = this.editor?.view?.dom || null

    if (this.editorElement) {
      // Use capture phase for better event handling
      this.editorElement.addEventListener('mousedown', this.handleCalloutMousedown, true)
      this.editorElement.addEventListener('click', this.handleCalloutClick, true)
      this.editorElement.addEventListener('click', this.handleVariablePlaceholderClick, true)
    }

    this.scheduleVariableIconSync()
  }

  beforeDestroy(): void {
    this.stopStaticWatch?.()
    this.cancelEmbedWaits()
    // Safety net for parents that did not flush before destroying their editor
    this.flushPendingSave()
    this.autosaveManager?.stopTimer()
    this.editor?.off('transaction', this.onEditorTransaction)
    this.editor?.off('blur', this.onEditorBlur)
    this.pooledTarget = undefined

    // Clean up event listeners to prevent memory leaks
    if (this.editorElement) {
      this.editorElement.removeEventListener('mousedown', this.handleCalloutMousedown, true)
      this.editorElement.removeEventListener('click', this.handleCalloutClick, true)
      this.editorElement.removeEventListener('click', this.handleVariablePlaceholderClick, true)
      this.editorElement = null
    }
    if (this.iconSyncRaf !== null) {
      window.cancelAnimationFrame(this.iconSyncRaf)
      this.iconSyncRaf = null
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;
/* Basic editor styles */
.non-render {
  height: 0px;
}

.editor-wrapper ::v-deep {
  width: 100%;
  position: relative;
  padding: 0 $notch_space_05x;

  // Rich-text iframes before they are loaded (static content and the iframe node view)
  @include notch-embed-card;

  // A rich-text embed shown through a card: the card and the player it loads share one player-sized box, so the
  // text below does not move when the player replaces the card
  .iframe-wrapper--click-to-load {
    position: relative;
    max-width: 640px;

    .embed-card {
      aspect-ratio: 16 / 9;
    }

    .embed-card--loading {
      position: absolute;
      inset: 0;
      z-index: 1;
    }

    .embed-iframe {
      display: block;
      width: 100%;
      aspect-ratio: 16 / 9;
      height: auto;
      border: 0;
      border-radius: $notch-radius-2x;
    }
  }

  // A link preview card in a table cell (tiptap-bookmark.ts): favicon and host under the title, cover on the right
  .notch-bookmark {
    margin: $notch-space-1x 0;
    max-width: 640px;

    .notch-bookmark__link {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      column-gap: $notch-space-2x;
      align-items: center;
      padding: $notch-space-3x;
      border: 1px solid $notch-color-neutral-sand-200;
      border-radius: $notch-radius-2x;
      background: $notch-color-neutral-sand-50;
      color: $notch-color-neutral-grey-900;
      text-decoration: none;
      cursor: pointer;

      &:hover {
        background: $notch-color-neutral-sand-100;
      }
    }

    .notch-bookmark__title {
      grid-column: 1 / 3;
      @include notch-text-paragraph-semibold-S;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .notch-bookmark__description {
      grid-column: 1 / 3;
      @include notch-text-paragraph-regular-S;
      color: $notch-color-neutral-sand-600;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .notch-bookmark__favicon {
      grid-column: 1;
      grid-row: 3;
      width: 16px;
      height: 16px;
      object-fit: contain;
    }

    .notch-bookmark__url {
      grid-column: 2;
      grid-row: 3;
      @include notch-text-paragraph-regular-S;
      color: $notch-color-neutral-sand-600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .notch-bookmark__image {
      grid-column: 3;
      grid-row: 1 / 4;
      width: 96px;
      height: 64px;
      object-fit: cover;
      border-radius: $notch-radius-1x;
    }
  }

  // can not be part of the ProseMirror styles below because they are too unspecific.
  .ProseMirror {
    // replaces (not merges with) the inherited value, so it has to switch `calt` off itself: "(1)" → "①"
    font-feature-settings: 'ss02', 'ss05', 'liga' 0, 'calt' 0;

    .mention {
      @include notch-text-paragraph-semibold-S;
      color: $notch-color-neutral-grey-900;
      background-color: $notch-color-neutral-sand-100;
      border-radius: $notch-radius-2x;
      padding: $notch-space-1x $notch-space-2x;
      page-break-inside: avoid;
    }

    .variable-wrapper {
      position: relative;
      display: inline-block;
      page-break-inside: avoid;

      // Show callout on hover of wrapper or callout itself
      &:hover .assign-callout {
        display: flex;
        pointer-events: auto;
      }
    }

    .variable-placeholder {
      // Base/fallback styles
      background-color: #e8f0fe;
      border: 1px dashed #4c9aff;
      border-radius: 10px;
      padding: 5px 8px;
      display: inline-block;
      page-break-inside: avoid;
      @include notch-text-label-light-S;
      color: #1a73e8;
      font-weight: 500;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;

      // Variables WITH a value - green theme (default only OR recipient filled)
      &[data-has-default='true'] {
        background-color: #e6f4ea;
        border-color: #34a853;
        color: #1e7f43;
      }

      // Variables WITHOUT value and WITHOUT recipient - amber theme
      &[data-has-default='false']:not([data-has-recipient='true']) {
        background-color: #fffbeb;
        border-color: #fbbf24;
        color: #92400e;
      }

      // Variables WITH recipient assigned but NO value yet - blue theme
      &[data-has-default='false'][data-has-recipient='true'] {
        background-color: #e8f0fe;
        border-color: #4c9aff;
        color: #1a73e8;
      }

      // Preview/external: variables assigned to someone else (greyed out)
      &[data-assigned-to-current-user='false'][data-has-recipient='true'] {
        background-color: $notch-color-neutral-grey-200;
        border-color: $notch-color-neutral-grey-400;
        color: $notch-color-neutral-grey-600;
      }

      // Editable inner span for new variables
      .variable-inner {
        user-select: text;
        -webkit-user-select: text;
        -moz-user-select: text;
        -ms-user-select: text;
        outline: none;
        display: inline-block;
        min-width: 60px;
      }

      &.icon-warn::before,
      &.icon-pend::before,
      &.icon-done::before {
        display: inline-block;
        margin-right: 4px;
      }

      &.icon-warn::before {
        content: '⚠️';
      }

      &.icon-pend::before {
        content: attr(data-avatar-letter);
        width: 16px;
        height: 16px;
        line-height: 16px;
        text-align: center;
        border-radius: 999px;
        background-color: $notch-color-brand-powder;
        color: #ffffff;
        font-size: 10px;
        font-weight: 600;
        border: 1px solid rgba(0, 0, 0, 0.18);
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.5) inset;
      }
      &[data-assigned-to-current-user='false'].icon-pend::before {
        background-color: $notch-color-neutral-grey-400;
      }

      $avatar-color-map: (
        0: $notch-color-avatar-orange,
        1: $notch-color-avatar-purple,
        2: $notch-color-avatar-green,
        3: $notch-color-avatar-yellow,
        4: $notch-color-avatar-red,
        5: $notch-color-brand-powder,
        6: $notch-color-brand-lavender,
        7: $notch-color-brand-dusk,
        8: $notch-color-brand-harvest,
        9: $notch-color-brand-rose,
        10: $notch-color-brand-sky,
        11: $notch-color-brand-pistachio,
        12: $notch-color-brand-taupe,
        13: $notch-color-brand-silver,
        14: $notch-color-brand-scallop,
        15: $notch-color-brand-salvia
      );

      @each $index, $color in $avatar-color-map {
        &.icon-pend[data-avatar-color-index='#{$index}']::before {
          background-color: $color;
        }
      }

      &.icon-done::before {
        content: '✅';
      }

      // Handle suggestion decoration inside variable placeholder
      .variable-suggestion-decoration {
        display: inline;
        background: transparent;
        border: none;
        padding: 0;
        margin: 0;
      }
    }

    .assign-callout {
      position: absolute;
      top: -33px;
      left: 80%;
      transform: translateX(-50%);
      background: #ffffff;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      padding: 8px 10px;
      font-size: 12px;
      color: #374151;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      display: none;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      pointer-events: auto;
      cursor: pointer;
      transition: display 0.2s ease-in-out;
      z-index: 25; // Above $notch-layer-global-nav (20) but below feature restriction bar (30)
      // Add invisible padding area to bridge the gap between wrapper and callout
      margin-top: -5px;
      user-select: none;

      // The assignment is saving: stay visible after the pointer leaves, dim, and spin until it settles
      &.is-pending {
        display: flex;
        opacity: 0.7;
        cursor: progress;
        pointer-events: none;

        .callout-spinner {
          width: 12px;
          height: 12px;
          flex-shrink: 0;
          border: 2px solid #d1d5db;
          border-top-color: #374151;
          border-radius: 50%;
          animation: assign-callout-spin 0.8s linear infinite;

          @media (prefers-reduced-motion: reduce) {
            animation-duration: 3s;
          }
        }
      }
      // Performance optimizations
      will-change: opacity;
      backface-visibility: hidden;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;

      // Icon background (user-add icon from heroicons) - only show when no recipient
      &:not([data-recipient-email]) {
        padding-left: 28px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: 10px center;
        background-size: 14px 14px;
      }

      // Avatar styling when recipient exists
      .callout-avatar {
        width: 16px;
        height: 16px;
        min-width: 16px;
        min-height: 16px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        @include notch-text-overline-S;
        color: $notch-color-neutral-grey-900;
        font-weight: 500;

        // Color classes matching UserAvatar
        &.color-index-0 {
          background-color: $notch-color-avatar-orange;
        }
        &.color-index-1 {
          background-color: $notch-color-avatar-purple;
        }
        &.color-index-2 {
          background-color: $notch-color-avatar-green;
        }
        &.color-index-3 {
          background-color: $notch-color-avatar-yellow;
        }
        &.color-index-4 {
          background-color: $notch-color-avatar-red;
        }
        &.color-index-5 {
          background-color: $notch-color-brand-powder;
        }
        &.color-index-6 {
          background-color: $notch-color-brand-lavender;
        }
        &.color-index-7 {
          background-color: $notch-color-brand-dusk;
        }
        &.color-index-8 {
          background-color: $notch-color-brand-harvest;
        }
        &.color-index-9 {
          background-color: $notch-color-brand-rose;
        }
        &.color-index-10 {
          background-color: $notch-color-brand-sky;
        }
        &.color-index-11 {
          background-color: $notch-color-brand-pistachio;
        }
        &.color-index-12 {
          background-color: $notch-color-brand-taupe;
        }
        &.color-index-13 {
          background-color: $notch-color-brand-silver;
        }
        &.color-index-14 {
          background-color: $notch-color-brand-scallop;
        }
        &.color-index-15 {
          background-color: $notch-color-brand-salvia;
        }
      }

      .callout-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      // Horizontal kebab icon when recipient exists
      &[data-recipient-email] {
        padding-right: 28px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 12h.01M12 12h.01M18 12h.01' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
        background-size: 14px 14px;
      }

      // Pointer border (outer triangle)
      &::before {
        content: '';
        position: absolute;
        bottom: -9px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-top: 9px solid #d1d5db;
      }

      // Pointer fill (inner triangle)
      &::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid #ffffff;
      }
    }

    .caret-anchor {
      display: inline-block;
      min-width: 1px;
      contenteditable: false;
    }

    p {
      @include notch-text-paragraph-regular-S;
      page-break-inside: avoid;
      margin-bottom: $notch-space-2x;

      &.small {
        @include notch-text-label-light-XS;
      }
    }

    h1 {
      font-size: 1.5rem;
      font-weight: bold;
      page-break-inside: avoid;
      margin-top: $notch-space-6x;
      margin-bottom: $notch-space-1_5x;
    }

    h2 {
      font-size: 1.25rem;
      font-weight: bold;
      page-break-inside: avoid;
      margin-top: $notch-space-5x;
      margin-bottom: $notch-space-1x;
    }

    h3 {
      font-size: 1.125rem;
      font-weight: bold;
      page-break-inside: avoid;
      margin-top: $notch-space-5x;
      margin-bottom: $notch-space-1x;
    }

    a {
      color: $notch-color-neutral-grey-600;
      text-decoration: underline;
      overflow-wrap: anywhere;
    }

    // Rich-text iframes in the PDF export (static content and the iframe node view); after the link rule above,
    // and as specific, so the card's link looks as it does on embed elements
    @include notch-print-placeholder;

    blockquote {
      padding-left: $notch-space-4x;
      border-left: $notch-space-05x solid $notch-color-neutral-sand-200;
    }

    li {
      page-break-inside: avoid;

      p {
        margin-bottom: $notch-space-1x;
      }
    }

    ul {
      padding: 0 $notch-space-6x;

      li {
        list-style: disc;
        &:has(> h1)::marker {
          @include notch-text-heading-semibold-L;
        }
        &:has(> h2)::marker {
          @include notch-text-heading-semibold-M;
        }
        &:has(> h3)::marker {
          @include notch-text-heading-semibold-S;
        }
      }
      li li {
        list-style: circle;
      }
      li li li {
        list-style: square;
      }
    }

    @include notch-rich-text-ordered-lists;

    ul[data-type='taskList'] {
      padding: 0;
      page-break-inside: avoid;

      li {
        display: flex;
        overflow-wrap: anywhere;

        p {
          min-width: 1px;
        }
      }

      label {
        max-height: $notch-space-6x;
        display: flex;
      }

      input[type='checkbox'] {
        appearance: none;
        border: 2px solid;
        border-color: $notch-color-neutral-sand-200;
        width: $notch-space-4x;
        height: $notch-space-4x;
        border-radius: $notch-radius-1x;
        transition: border-color 0.2s, color 0.2s, border-width 0.3s ease-out;
        margin: $notch-space-05x $notch-space-2x 0 0;

        &:hover {
          border-color: $notch-color-neutral-sand-400;
          cursor: pointer;
        }
      }

      input:not(:checked)[type='checkbox'] {
        &:focus {
          box-shadow: 0px 0px 0px 2px $notch-color-semantic-accent-500;
        }
      }

      input:checked[type='checkbox'] {
        background: $notch-color-neutral-grey-900;
        border-color: $notch-color-neutral-grey-900;
        background-image: url('../../../assets/svg/check.svg');
        background-position: center;
      }
    }

    // Static task items only show their state (renderStaticHtml disables the box): the click reaches the item and
    // opens the editor instead of toggling a box nothing saves
    &.static-content ul[data-type='taskList'] input[type='checkbox'] {
      pointer-events: none;
    }

    > :last-child {
      margin-bottom: 0;
    }

    // The Placeholder extension's decoration, also rendered on an empty static block. Floated with no height,
    // so a placeholder longer than one line overflows instead of making the empty block taller.
    > .is-editor-empty::before {
      content: attr(data-placeholder);
      float: left;
      height: 0;
      color: $notch-color-neutral-sand-500;
      pointer-events: none;
      white-space: pre-line;
    }
  }
}

// grey-600 is too faint on paper: the PDF export prints text links in the same blue as the embed cards' links
.editor-wrapper.is-export ::v-deep .ProseMirror a {
  color: $notch-color-semantic-info-800;
}

.editor-wrapper.is-readonly ::v-deep {
  .assign-callout {
    display: none !important;
  }

  .variable-wrapper:hover .assign-callout {
    display: none !important;
    pointer-events: none;
  }
}

.no-heading-margins ::v-deep {
  .ProseMirror {
    h1,
    h2,
    h3 {
      margin-top: 0;
    }
  }
}

.large ::v-deep {
  .ProseMirror {
    .mention {
      @include notch-text-paragraph-semibold-M;
    }

    h1 {
      @include notch-text-heading-semibold-L;
    }

    h2 {
      @include notch-text-heading-semibold-M;
    }

    h3 {
      @include notch-text-heading-semibold-S;
    }

    p {
      @include notch-text-paragraph-regular-M;

      &.small {
        @include notch-text-paragraph-regular-S;
      }
    }

    b {
      @include notch-text-paragraph-semibold-M;
    }
  }
}

.small ::v-deep {
  .ProseMirror {
    .mention {
      @include notch-text-label-strong-XS;
    }

    p {
      @include notch-text-label-light-XS;

      &.small {
        @include notch-text-label-light-XXS;
      }
    }

    b {
      @include notch-text-label-light-XS;
    }
  }
}

.single-line ::v-deep {
  .ProseMirror {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    p {
      display: inline;
    }

    p:after {
      content: ' ';
    }

    br {
      content: ' ';
    }

    ul,
    ol {
      display: inline;
      list-style: none;
      padding: 0;
    }

    ul li,
    ol li {
      display: inline;
    }

    ul li:after,
    ol li:after {
      content: '. ';
    }
  }
}

// A static block is never focused, so it follows the same rules as an editor at rest
.editor-wrapper.placeholder-on-focus-only ::v-deep .ProseMirror:not(.ProseMirror-focused) > .is-editor-empty::before,
.editor-wrapper.placeholder-hidden-on-focus ::v-deep .ProseMirror-focused > .is-editor-empty::before {
  content: none;
}

@keyframes assign-callout-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
